import { cn } from "@u/cn";
import { Container } from "./Container";
import { SectionHeader } from "./SectionHeader";

const PAD = {
  sm: "py-16",
  md: "py-20 md:py-24",
  lg: "py-24 md:py-28",
} as const;

interface SectionProps {
  /** Mono uppercase eyebrow above the title. */
  eyebrow?: string;
  /** Segundo término del eyebrow, separado por `//` (ver SectionHeader). */
  eyebrowContext?: string;
  /** Section title (renders the shared SectionHeader). */
  title?: string;
  /** Highlighted span appended to the title (primary color). */
  highlight?: string;
  /** Supporting copy under the title. */
  description?: string;
  /** Heading level for the title. Defaults to h2. Use "h3" when nested under another section heading. */
  as?: "h2" | "h3" | "h4";
  id?: string;
  /** Top border divider. Default true. */
  bordered?: boolean;
  /** Vertical rhythm token. Default "lg". */
  pad?: keyof typeof PAD;
  /** Center the header block. */
  centered?: boolean;
  /** Horizontal alignment of the header block. Overrides `centered`. */
  align?: "left" | "center" | "right";
  /**
   * Ambient radial glow behind the section (top-anchored navy wash, like
   * HomePage). Implies `relative overflow-hidden`. Pass a raw CSS gradient
   * string for a custom wash (rendered full-bleed at section level).
   */
  glow?: boolean | "primary" | "warm" | string;
  /**
   * Fondo de la sección. `"brand"` pinta la banda navy de marca (utilidad
   * `brand-band`, que remapea los tokens dentro) — el ritmo claro/navy que ya
   * tiene el feed de Instagram. Reservado a secciones ancla: dos por landing.
   * Los CTA dentro de una banda van `design="outline"` (blanco sobre navy);
   * el fill `primary-strong` con texto blanco no llega a AA en small text.
   */
  tone?: "default" | "brand";
  /**
   * Isotipo gigante recortado en la esquina inferior derecha, al 7-8% — la
   * marca de agua que llevan todas las piezas de redes. Solo decorativo.
   */
  watermark?: boolean;
  /** Scroll-reveal del header (fade + slide up). Opt-in — landings. */
  reveal?: boolean;
  /** Container width. */
  size?: React.ComponentProps<typeof Container>["size"];
  className?: string;
  /** Extra classes for the inner container. */
  containerClassName?: string;
  children?: React.ReactNode;
}

/**
 * Canonical marketing/public section wrapper.
 * Replaces the inline `<section className="border-t border-border/20 py-X">`
 * + `<div className="max-w-7xl mx-auto px-6 sm:px-12">` pattern (~24 copies).
 */
export function Section({
  eyebrow,
  eyebrowContext,
  title,
  highlight,
  description,
  as,
  id,
  bordered = true,
  pad = "lg",
  centered = false,
  align,
  glow = false,
  tone = "default",
  watermark = false,
  reveal = false,
  size,
  className,
  containerClassName,
  children,
}: SectionProps) {
  const hasHeader = eyebrow || title || description;
  const brand = tone === "brand";
  const customGlow =
    typeof glow === "string" && glow !== "primary" && glow !== "warm";
  const glowClass =
    glow === "warm"
      ? "bg-[radial-gradient(ellipse_70%_40%_at_50%_0%,rgba(180,83,9,0.05),transparent)]"
      : "bg-[radial-gradient(ellipse_80%_40%_at_50%_0%,rgba(12,48,89,0.07),transparent)]";

  return (
    <section
      id={id}
      className={cn(
        "w-full",
        bordered && "border-t border-border/20",
        (glow || watermark) && "relative overflow-hidden",
        brand && "brand-band",
        PAD[pad],
        className,
      )}
    >
      {watermark && (
        <img
          aria-hidden
          alt=""
          loading="lazy"
          decoding="async"
          src={brand ? "/logos/icon-white.svg" : "/logos/icon-color.svg"}
          className="pointer-events-none absolute -bottom-24 -right-28 w-[28rem] max-w-[60%] opacity-[0.07] select-none"
        />
      )}
      {glow && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0",
            !customGlow && glowClass,
          )}
          style={customGlow ? { backgroundImage: glow as string } : undefined}
        />
      )}
      <Container size={size} className={cn("relative", containerClassName)}>
        {hasHeader && (
          <SectionHeader
            eyebrow={eyebrow}
            eyebrowContext={eyebrowContext}
            title={title ?? ""}
            highlight={highlight}
            description={description}
            as={as}
            reveal={reveal}
            className={cn(
              "mb-16",
              (align === "center" || (centered && !align)) &&
                "text-center mx-auto [&_p]:mx-auto",
              align === "right" && "flex flex-col items-end text-right",
            )}
          />
        )}
        {children}
      </Container>
    </section>
  );
}
